import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import type { AgentRun, AgentStep } from "@flowgen/shared";

interface RunDetail {
  run: AgentRun;
  steps: AgentStep[];
}

export function AgentTab() {
  const toast = useToast();
  const [pitch, setPitch] = useState(
    "Children's video based on the following story outline:\nA curious bunny finds a glowing seed in the forest, plants it with a shy fox, and they watch a kindness tree grow overnight."
  );
  const [pauseBeforeVideo, setPauseBeforeVideo] = useState(true);
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [repairSceneId, setRepairSceneId] = useState("");
  const [repairStoryboard, setRepairStoryboard] = useState("");

  async function reloadRuns() {
    const list = await api.get<AgentRun[]>("/agent/runs");
    setRuns(list);
    if (!activeId && list[0]) setActiveId(list[0].id);
  }

  async function reloadDetail(id: string) {
    const d = await api.get<RunDetail>(`/agent/runs/${id}`);
    setDetail(d);
  }

  useEffect(() => {
    reloadRuns().catch((err: any) => toast.show(err?.message ?? "Failed to load runs", "error"));
  }, []);

  useEffect(() => {
    if (!activeId) {
      setDetail(null);
      return;
    }
    reloadDetail(activeId).catch(() => undefined);
    const t = setInterval(() => {
      reloadDetail(activeId).catch(() => undefined);
      reloadRuns().catch(() => undefined);
    }, 2500);
    return () => clearInterval(t);
  }, [activeId]);

  async function startRun() {
    if (!pitch.trim()) {
      toast.show("Enter a pitch", "error");
      return;
    }
    setBusy(true);
    try {
      const d = await api.post<RunDetail>("/agent/runs", {
        pitch: pitch.trim(),
        pauseBeforeVideo,
      });
      setActiveId(d.run.id);
      setDetail(d);
      await reloadRuns();
      toast.show("Agent run started — watch Characters / Scenes fill in", "success");
    } catch (err: any) {
      toast.show(err?.details?.message ?? err?.message ?? "Start failed", "error");
    } finally {
      setBusy(false);
    }
  }

  async function approve() {
    if (!activeId) return;
    setBusy(true);
    try {
      const d = await api.post<RunDetail>(`/agent/runs/${activeId}/approve`, {});
      setDetail(d);
      toast.show("Approved — generating video…", "success");
    } catch (err: any) {
      toast.show(err?.message ?? "Approve failed", "error");
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!activeId) return;
    setBusy(true);
    try {
      const d = await api.post<RunDetail>(`/agent/runs/${activeId}/cancel`, {});
      setDetail(d);
      toast.show("Run cancelled", "success");
    } catch (err: any) {
      toast.show(err?.message ?? "Cancel failed", "error");
    } finally {
      setBusy(false);
    }
  }

  async function repair() {
    if (!repairSceneId.trim()) {
      toast.show("Scene id required", "error");
      return;
    }
    setBusy(true);
    try {
      await api.post("/agent/repair-scene", {
        sceneId: repairSceneId.trim(),
        storyboard: repairStoryboard.trim() || undefined,
      });
      toast.show("Repair finished", "success");
    } catch (err: any) {
      toast.show(err?.details?.message ?? err?.message ?? "Repair failed", "error");
    } finally {
      setBusy(false);
    }
  }

  const run = detail?.run;
  const steps = detail?.steps ?? [];
  const gate = run?.gatePayload as
    | { sceneCount?: number; totalSeconds?: number; scenes?: { title: string; durationSeconds: number }[] }
    | null
    | undefined;

  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <h2 style={{ margin: 0 }}>Agent</h2>
          <p className="muted" style={{ margin: "6px 0 0" }}>
            Pitch → outline → cast → scenes → (approve) → generate → edit export. Artifacts land in the other tabs.
          </p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div>
          <label className="tiny muted">Pitch</label>
          <textarea
            value={pitch}
            onChange={(e) => setPitch(e.target.value)}
            rows={10}
            style={{ width: "100%", fontFamily: "inherit" }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
            <input
              type="checkbox"
              checked={pauseBeforeVideo}
              onChange={(e) => setPauseBeforeVideo(e.target.checked)}
            />
            Pause before queuing video generation
          </label>
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button className="primary" disabled={busy} onClick={startRun}>
              {busy ? "Working…" : "Start run"}
            </button>
          </div>

          <div style={{ marginTop: 24 }}>
            <div className="section-title">Past runs</div>
            {runs.length === 0 && <div className="muted tiny">No runs yet.</div>}
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {runs.map((r) => (
                <li key={r.id}>
                  <button
                    className={r.id === activeId ? "primary" : undefined}
                    style={{ width: "100%", textAlign: "left", marginBottom: 6 }}
                    onClick={() => setActiveId(r.id)}
                  >
                    <span style={{ fontWeight: 600 }}>{r.status}</span>
                    <span className="muted tiny" style={{ display: "block" }}>
                      {r.pitch.slice(0, 80)}
                      {r.pitch.length > 80 ? "…" : ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div style={{ marginTop: 24 }}>
            <div className="section-title">Repair scene</div>
            <input
              placeholder="Scene id"
              value={repairSceneId}
              onChange={(e) => setRepairSceneId(e.target.value)}
              style={{ width: "100%", marginBottom: 8 }}
            />
            <textarea
              placeholder="Optional new storyboard"
              value={repairStoryboard}
              onChange={(e) => setRepairStoryboard(e.target.value)}
              rows={3}
              style={{ width: "100%" }}
            />
            <button style={{ marginTop: 8 }} disabled={busy} onClick={repair}>
              Re-generate scene
            </button>
          </div>
        </div>

        <div>
          {!run && <div className="muted">Select or start a run.</div>}
          {run && (
            <>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <StatusPill status={run.status} />
                {run.pendingGate && (
                  <span className="tiny" style={{ color: "var(--warning)" }}>
                    Gate: {run.pendingGate}
                  </span>
                )}
                {run.status === "awaiting_approval" && (
                  <button className="primary" disabled={busy} onClick={approve}>
                    Approve & generate
                  </button>
                )}
                {(run.status === "running" ||
                  run.status === "planning" ||
                  run.status === "awaiting_approval") && (
                  <button disabled={busy} onClick={cancel}>
                    Cancel
                  </button>
                )}
              </div>

              {run.errorMessage && (
                <div style={{ color: "var(--danger)", marginTop: 8 }}>{run.errorMessage}</div>
              )}

              {gate && run.pendingGate === "before_video" && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    border: "1px solid var(--warning)",
                    borderRadius: "var(--radius)",
                  }}
                >
                  <strong>Ready to generate video</strong>
                  <div className="tiny muted">
                    {gate.sceneCount} scenes · ~{gate.totalSeconds}s of clips
                  </div>
                  <ul className="tiny" style={{ marginBottom: 0 }}>
                    {(gate.scenes ?? []).map((s, i) => (
                      <li key={i}>
                        {s.title} ({s.durationSeconds}s)
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {run.planJson && (
                <div style={{ marginTop: 16 }}>
                  <div className="section-title">Plan</div>
                  <pre
                    className="tiny"
                    style={{
                      maxHeight: 180,
                      overflow: "auto",
                      background: "rgba(0,0,0,0.25)",
                      padding: 10,
                      borderRadius: 6,
                    }}
                  >
                    {JSON.stringify(run.planJson, null, 2)}
                  </pre>
                </div>
              )}

              {run.notes.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div className="section-title">Notes</div>
                  <ul className="tiny">
                    {run.notes.map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div style={{ marginTop: 16 }}>
                <div className="section-title">Steps</div>
                <ol style={{ paddingLeft: 18, margin: 0 }}>
                  {steps.map((s) => (
                    <li key={s.id} style={{ marginBottom: 8 }}>
                      <code className="tiny">{s.primitive}</code>
                      {s.routine && (
                        <span className="tiny muted"> · {s.routine}</span>
                      )}
                      <span className="tiny" style={{ marginLeft: 8 }}>
                        {s.status}
                      </span>
                      {s.errorMessage && (
                        <div className="tiny" style={{ color: "var(--danger)" }}>
                          {s.errorMessage}
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const color =
    status === "completed"
      ? "var(--success)"
      : status === "failed" || status === "cancelled"
        ? "var(--danger)"
        : status === "awaiting_approval"
          ? "var(--warning)"
          : "#fff";
  return (
    <span
      className="topbar-pill"
      style={{ border: `1px solid ${color}`, color, fontWeight: 700 }}
    >
      {status}
    </span>
  );
}
